import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  SET_EMPLOYEE_SCHEDULE_OVERRIDE,
  CLEAR_EMPLOYEE_SCHEDULE_OVERRIDE,
} from '../../../graphql/team-availability-mutations';

const STATUS_WORKING_ON_ISLAND = 'WORKING_ON_ISLAND';
const STATUS_WORKING_REMOTE = 'WORKING_REMOTE';
const STATUS_OFF = 'OFF';

const STATUS_OPTIONS = [
  {
    value: STATUS_WORKING_ON_ISLAND,
    label: 'Working — On-Island',
    description: 'Will be working in person this day.',
    activeClass: 'bg-sky-600 text-white border-sky-600',
    inactiveClass: 'bg-white text-gray-700 border-gray-300 hover:border-sky-300',
  },
  {
    value: STATUS_WORKING_REMOTE,
    label: 'Working — Remote',
    description: 'Will be working remotely this day.',
    activeClass: 'bg-purple-600 text-white border-purple-600',
    inactiveClass: 'bg-white text-gray-700 border-gray-300 hover:border-purple-300',
  },
  {
    value: STATUS_OFF,
    label: 'Off',
    description: "Won't be working this day.",
    activeClass: 'bg-gray-600 text-white border-gray-600',
    inactiveClass: 'bg-white text-gray-700 border-gray-300 hover:border-gray-400',
  },
];

const deriveStatusValue = ({ isWorking, isRemote }) => {
  if (isWorking !== true) return STATUS_OFF;
  return isRemote === true ? STATUS_WORKING_REMOTE : STATUS_WORKING_ON_ISLAND;
};

const buildInputFromStatus = (status) => ({
  isWorking: status !== STATUS_OFF,
  isRemote: status === STATUS_WORKING_REMOTE,
});

const toDateKey = (value) => {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toCalendarDateInput = (value) => `${toDateKey(value)}T12:00:00.000Z`;

function AdminUserDayOverrideModal({
  isOpen,
  userId,
  date,
  effectiveStatus,
  hasOverride,
  isBlocked,
  blockedReason,
  onClose,
  onSaved,
}) {
  const [selectedStatus, setSelectedStatus] = useState(STATUS_WORKING_ON_ISLAND);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedStatus(deriveStatusValue(effectiveStatus || {}));
    setNotes(effectiveStatus?.notes || '');
  }, [isOpen, effectiveStatus]);

  const [saveOverride, { loading: saving }] = useMutation(SET_EMPLOYEE_SCHEDULE_OVERRIDE, {
    onCompleted: () => {
      toast.success('Day updated');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update day');
    },
  });

  const [clearOverride, { loading: clearing }] = useMutation(CLEAR_EMPLOYEE_SCHEDULE_OVERRIDE, {
    onCompleted: () => {
      toast.success('Reverted to weekly schedule');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reset day');
    },
  });

  if (!isOpen || !date || !userId) return null;

  const handleSave = () => {
    const { isWorking, isRemote } = buildInputFromStatus(selectedStatus);
    saveOverride({
      variables: {
        userId,
        input: {
          date: toCalendarDateInput(date),
          isWorking,
          isRemote,
          notes: notes.trim() || null,
        },
      },
    });
  };

  const handleReset = () => {
    clearOverride({ variables: { userId, date: toCalendarDateInput(date) } });
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${format(date, 'EEEE, MMM d, yyyy')}`}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{format(date, 'EEEE')}</h3>
              <p className="text-sm text-gray-500">{format(date, 'MMMM d, yyyy')}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isBlocked ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              {blockedReason || "This day can't be overridden."}
              <p className="mt-2 text-xs text-amber-700">
                Holidays and approved time off take precedence over per-day overrides.
                Remove the time-off entry if this teammate needs to work this day.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Set the status for this specific day. Saving creates an override that
                replaces the usual weekly schedule for this date only.
              </p>

              <div className="space-y-2 mb-4">
                {STATUS_OPTIONS.map((option) => {
                  const isSelected = selectedStatus === option.value;
                  const stateClass = isSelected ? option.activeClass : option.inactiveClass;
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => setSelectedStatus(option.value)}
                      aria-pressed={isSelected}
                      className={`w-full text-left border rounded-lg px-3 py-2 transition-colors ${stateClass}`}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      <div
                        className={`text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}
                      >
                        {option.description}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mb-4">
                <label
                  htmlFor="admin-day-override-notes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="admin-day-override-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={2}
                  placeholder="Anything the team should know about this day"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
              </div>

              {hasOverride && (
                <p className="text-xs text-purple-700 bg-purple-50 border border-purple-100 rounded-md p-2 mb-4">
                  This day is currently overriding the weekly schedule. Save to update
                  the override, or reset to fall back to the usual schedule.
                </p>
              )}
            </>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              {hasOverride && !isBlocked && (
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={clearing}
                  className="text-sm font-medium text-gray-600 hover:text-gray-800 underline disabled:opacity-50"
                >
                  Reset to weekly default
                </button>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {isBlocked ? 'Close' : 'Cancel'}
              </button>
              {!isBlocked && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminUserDayOverrideModal;
